import React, { useState, useEffect, useCallback } from 'react';
import { api, endpoints } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import './OrganizationsPage.css'; // Aynı CSS'i kullan

const ACTION_LABELS = {
  'organization.create': 'Organizasyon oluşturuldu',
  'organization.update': 'Organizasyon güncellendi',
  'organization.delete': 'Organizasyon silindi',
  'organization.activate': 'Organizasyon aktifleştirildi',
  'organization.deactivate': 'Organizasyon devre dışı bırakıldı',
  'license.create': 'Lisans oluşturuldu',
  'license.update': 'Lisans güncellendi',
  'user.role_change': 'Kullanıcı rolü değiştirildi',
  'user.deactivate': 'Kullanıcı devre dışı bırakıldı',
  'registration.approve': 'Kayıt onaylandı',
  'registration.reject': 'Kayıt reddedildi',
};

const TARGET_TYPES = ['Organization', 'License', 'User', 'OrganizationRegistration'];

const AuditLogPage = () => {
  const { logout } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('');

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (actionFilter) params.action = actionFilter;
      if (targetTypeFilter) params.target_type = targetTypeFilter;
      const data = await api.get(endpoints.admin.auditLog.list, { params });
      setEntries(data.results || data || []);
    } catch (err) {
      console.error('Error fetching audit log:', err);
      setError(err.response?.data?.error || 'Aktivite günlüğü yüklenemedi');
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [actionFilter, targetTypeFilter, logout]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const filteredEntries = entries.filter(e =>
    e.actor_repr.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.target_repr.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('tr-TR');
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>Aktivite Günlüğü</h1>
          <p className="page-header-subtitle">Yıkıcı/hassas admin aksiyonlarının denetlenebilir kaydı — salt okunur</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchEntries}>
            ↻ Yenile
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Kapat</button>
        </div>
      )}

      <div className="table-container">
        <div className="table-header">
          <h2>Kayıtlar ({filteredEntries.length})</h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="Aktör veya hedef ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="audit-log-filters-row">
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="">Tüm aksiyonlar</option>
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select value={targetTypeFilter} onChange={(e) => setTargetTypeFilter(e.target.value)}>
            <option value="">Tüm hedef tipleri</option>
            {TARGET_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {filteredEntries.length === 0 ? (
          <div className="empty-state">
            <h3>Kayıt Bulunamadı</h3>
            <p>Seçili filtrelerle eşleşen bir aktivite kaydı yok</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Aktör</th>
                <th>Aksiyon</th>
                <th>Hedef</th>
                <th>Detay</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDate(entry.created_at)}</td>
                  <td>{entry.actor_repr}</td>
                  <td>{entry.action_display}</td>
                  <td>{entry.target_repr || '—'}</td>
                  <td className="audit-log-detail">
                    {entry.detail && Object.keys(entry.detail).length > 0
                      ? JSON.stringify(entry.detail)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
